import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import type { RecencyFilter } from '@/types/db';
import { cn } from '@/lib/utils';

export interface RecencyRadioProps {
  value: RecencyFilter;
  onChange: (next: RecencyFilter) => void;
  className?: string;
}

const OPTIONS: { value: RecencyFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
];

/**
 * `<RecencyRadio>` — horizontal pill radio group for recency filter.
 *
 * UI-SPEC § 3.9 + § 8:
 * - Options: All (default), 7d, 30d, 90d.
 * - Selected pill: `bg-tide-400/20 border-tide-400/60 text-foam-50`.
 * - Unselected: transparent with `border-foam-50/15` and `text-foam-50/70`.
 * - Wrapped in shadcn-style Radix `<RadioGroup>` for keyboard nav (Arrow
 *   keys cycle, Space selects).
 *
 * ARIA: `<RadioGroup aria-label="Recency filter">` + `aria-label="New"`
 * on the visible "New" group label per UI-SPEC § 8 ("New" is the
 * group label, the four buttons sit under it).
 */
export function RecencyRadio({ value, onChange, className }: RecencyRadioProps) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className="text-sm font-semibold text-foam-50/80">New:</span>
      <RadioGroupPrimitive.Root
        value={value}
        onValueChange={(v) => onChange(v as RecencyFilter)}
        aria-label="Recency filter"
        className="flex items-center gap-1"
      >
        {OPTIONS.map((opt) => {
          const active = opt.value === value;
          return (
            <RadioGroupPrimitive.Item
              key={opt.value}
              value={opt.value}
              aria-label={`Last ${opt.label}`}
              className={cn(
                'h-7 px-3 rounded-full text-xs font-semibold border transition-colors duration-100',
                'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
                active
                  ? 'bg-tide-400/20 border-tide-400/60 text-foam-50'
                  : 'bg-transparent border-foam-50/15 text-foam-50/70 hover:text-foam-50',
              )}
            >
              {opt.label}
            </RadioGroupPrimitive.Item>
          );
        })}
      </RadioGroupPrimitive.Root>
    </div>
  );
}
